import React from 'react';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { DIMENSION_COLORS, DIMENSION_NAMES, SurveyRecord } from '../types';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface Props {
  records: SurveyRecord[];
}

const DIM_CODES = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'];

/**
 * 在顶点位置绘制标签：名称 + 分数
 * 用第一条记录的分数作为默认标签
 */
function createLabelPlugin(records: SurveyRecord[]) {
  const scores = records.length > 0
    ? DIM_CODES.map(code => records[0].dimensionScores[code] || 0)
    : [0, 0, 0, 0, 0, 0];

  const labels = DIM_CODES.map((code, i) => ({
    name: DIMENSION_NAMES[code],
    score: scores[i],
  }));

  return {
    id: 'vertexLabels',
    afterDraw(chart: any) {
      const { ctx, scales } = chart;
      const rScale = scales.r;
      if (!rScale) return;

      const centerX = rScale.xCenter;
      const centerY = rScale.yCenter;
      const count = labels.length;
      const labelRadius = rScale.drawingArea + 20; // 顶点外扩 20px

      labels.forEach((item, i) => {
        const angle = (Math.PI / 180) * ((360 / count) * i - 90);
        const x = centerX + labelRadius * Math.cos(angle);
        const y = centerY + labelRadius * Math.sin(angle);

        ctx.save();
        ctx.textBaseline = 'middle';

        // 维度名称（粗体）
        ctx.font = 'bold 14px "PingFang SC", sans-serif';
        ctx.fillStyle = '#374151';

        // 根据角度自适应对齐
        const deg = ((360 / count) * i) % 360;
        if (deg === 0) {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'bottom';
        } else if (deg < 180) {
          ctx.textAlign = 'left';
        } else if (deg > 180) {
          ctx.textAlign = 'right';
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
        }

        // 绘制维度名
        ctx.fillText(item.name, x, y);

        // 分数（小一号，颜色用维度色，但浅色加深以保证白底可读）
        let scoreColor = DIMENSION_COLORS[DIM_CODES[i]] || '#666';
        if (scoreColor === '#F1C40F') scoreColor = '#C8A010';
        ctx.font = 'bold 12px "PingFang SC", sans-serif';
        ctx.fillStyle = scoreColor;

        // 分数位置：在名称下方偏移
        const scoreOffset = deg === 0 ? 16 :
                           deg === 180 ? -16 :
                           deg < 90 || deg > 270 ? 16 :
                           deg < 180 ? 16 : -16;

        ctx.fillText(`${item.score}分`, x, y + scoreOffset);

        ctx.restore();
      });
    },
  };
}

const HappinessWheel: React.FC<Props> = ({ records }) => {
  if (records.length === 0) return null;

  const datasets = records.map((record, idx) => {
    const data = DIM_CODES.map(code => record.dimensionScores[code] || 0);
    const hue = (idx * 60) % 360;
    const color = idx === 0
      ? '#3498DB'
      : `hsl(${hue}, 70%, 50%)`;
    return {
      label: record.surveyDate,
      data,
      backgroundColor: idx === 0
        ? 'rgba(52, 152, 219, 0.15)'
        : `hsla(${hue}, 70%, 50%, 0.15)`,
      borderColor: color,
      pointBackgroundColor: color,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2,
    };
  });

  const chartData = {
    labels: DIM_CODES.map(code => ''), // 空标签，用自定义插件绘制
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: {
        top: 30,
        bottom: 30,
        left: 30,
        right: 30,
      },
    },
    scales: {
      r: {
        min: 0,
        max: 15,
        ticks: {
          stepSize: 3,
          font: { size: 11 },
          backdropColor: 'transparent',
        },
        grid: {
          color: 'rgba(0,0,0,0.08)',
        },
        angleLines: {
          color: 'rgba(0,0,0,0.12)',
        },
        pointLabels: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          padding: 20,
          font: { size: 13 },
          usePointStyle: true,
        },
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: ${ctx.raw}分`,
        },
      },
    },
  };

  const labelPlugin = createLabelPlugin(records);

  return (
    <Radar data={chartData} options={options} plugins={[labelPlugin]} />
  );
};

export default HappinessWheel;
